import { FC, ReactNode, RefObject, useMemo } from "react";
import { Trans as TransComponent, useLingui } from "@lingui/react";
import { Text } from "@otrium/atoms";
import { useBreakpoints } from "@otrium/core";
import { ProductVariation, SingleProduct } from "src/types/graphql.d";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { showMappedSizeSelector } from "src/molecules/Filter/components/Sizes/utils/sizeSchema";
import { sizeFitLabelMessages } from "src/hooks/useSizeSchemaFilter";
import { SizeFitLabelBanner } from "src/atoms/SizeFitLabelBanner";
import { ConversionItemAttributes } from "src/utils/constructor-beacon";
import { NewSizeSelector } from "./NewSizeSelector";
import { OldSizeSelector } from "./OldSizeSelector";

interface ProductSizeSelectorProps {
  product: SingleProduct;
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  onAddToCart: () => Promise<boolean>;
  addToCartLoading: boolean;
  hasSizeGuide?: boolean;
  onOpenSizeGuide?: () => void;
  dropdownHeader?: ReactNode;
  isSizeFitsSmall?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  defaultValue?: string;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
  withOverlay?: boolean;
  overlayExcludeRef?: RefObject<HTMLDivElement>;
  ariaDescribedBy?: string;
  onSizeSelectorOpen?: () => void;
  onSizeSelectorClose?: () => void;
  autoOpenSingleSize?: boolean;
  "data-testid"?: string;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
  cnstrcItem?: ConversionItemAttributes;
}

export const ProductSizeSelector: FC<ProductSizeSelectorProps> = ({
  product,
  selectedVariationId,
  onSelectVariation,
  onAddToCart,
  addToCartLoading,
  hasSizeGuide = false,
  onOpenSizeGuide,
  dropdownHeader,
  isSizeFitsSmall = false,
  hasError = false,
  disabled = false,
  defaultValue,
  selectedSchema,
  onSchemaChange,
  withOverlay = false,
  overlayExcludeRef,
  ariaDescribedBy,
  onSizeSelectorOpen,
  onSizeSelectorClose,
  autoOpenSingleSize,
  "data-testid": dataTestId = "product-size-selector",
  onBackInStockPopupChange,
  cnstrcItem,
}) => {
  const { i18n } = useLingui();
  const { isMobile } = useBreakpoints();
  const { sizeSelectorMappingsEnabled, showOutOfStockVariants } =
    useFeatureFlags();

  const sizeFitLabel = product?.size_fit_label;
  const showSizeFitBanner =
    sizeFitLabel === "runs_small" || sizeFitLabel === "runs_large";

  const variationsWithMappedSize = useMemo(
    () =>
      (product.variations as ProductVariation[] | undefined)?.filter(
        (v) => v.mapped_size && (showOutOfStockVariants || v.stock_quantity > 0)
      ) || [],
    [product.variations, showOutOfStockVariants]
  );

  const useNewSizeSelector = useMemo(
    () =>
      sizeSelectorMappingsEnabled &&
      showMappedSizeSelector(variationsWithMappedSize),
    [sizeSelectorMappingsEnabled, variationsWithMappedSize]
  );

  const sizeFitBanner =
    !isMobile &&
    showSizeFitBanner &&
    sizeFitLabel &&
    sizeFitLabelMessages[sizeFitLabel] ? (
      <SizeFitLabelBanner>
        <Text color="tone.black" fontSize={[0]}>
          <TransComponent
            id={sizeFitLabelMessages[sizeFitLabel].id}
            message={sizeFitLabelMessages[sizeFitLabel].message}
          />
        </Text>
      </SizeFitLabelBanner>
    ) : null;

  if (useNewSizeSelector) {
    return (
      <NewSizeSelector
        product={product}
        variations={variationsWithMappedSize}
        selectedVariationId={selectedVariationId}
        onSelectVariation={onSelectVariation}
        onAddToCart={onAddToCart}
        addToCartLoading={addToCartLoading}
        hasSizeGuide={hasSizeGuide}
        onOpenSizeGuide={onOpenSizeGuide}
        hasError={hasError}
        disabled={disabled}
        locale={i18n.locale}
        selectedSchema={selectedSchema}
        onSchemaChange={onSchemaChange}
        isMobile={isMobile}
        sizeFitLabel={sizeFitLabel}
        showSizeFitBanner={showSizeFitBanner}
        sizeFitBanner={sizeFitBanner}
        onSizeSelectorOpen={onSizeSelectorOpen}
        onSizeSelectorClose={onSizeSelectorClose}
        autoOpenSingleSize={autoOpenSingleSize}
        ariaDescribedBy={ariaDescribedBy}
        dataTestId={dataTestId}
        onBackInStockPopupChange={onBackInStockPopupChange}
        cnstrcItem={cnstrcItem}
      />
    );
  }

  return (
    <OldSizeSelector
      product={product}
      selectedVariationId={selectedVariationId}
      onSelectVariation={onSelectVariation}
      onAddToCart={onAddToCart}
      addToCartLoading={addToCartLoading}
      hasSizeGuide={hasSizeGuide}
      onOpenSizeGuide={onOpenSizeGuide}
      dropdownHeader={dropdownHeader}
      isSizeFitsSmall={isSizeFitsSmall}
      hasError={hasError}
      disabled={disabled}
      defaultValue={defaultValue}
      withOverlay={withOverlay}
      overlayExcludeRef={overlayExcludeRef}
      isMobile={isMobile}
      sizeFitLabel={sizeFitLabel}
      showSizeFitBanner={showSizeFitBanner}
      sizeFitBanner={sizeFitBanner}
      onSizeSelectorOpen={onSizeSelectorOpen}
      onSizeSelectorClose={onSizeSelectorClose}
      autoOpenSingleSize={autoOpenSingleSize}
      ariaDescribedBy={ariaDescribedBy}
      dataTestId={dataTestId}
      onBackInStockPopupChange={onBackInStockPopupChange}
      cnstrcItem={cnstrcItem}
    />
  );
};

export default ProductSizeSelector;
